<template>
  <PageHeader :title="$t('ui.entities.dealership')">
    <template #right>
      <div class="flex gap-2 items-center flex-col md:flex-row md:gap-4">
        <div v-if="dealershipsAllowed.length > 1">
          <Select
            v-model="dealershipSelected"
            :options="dealershipsAllowed"
            searchable
            value-key="id"
            label-key="name"
            placeholder="Select a Dealership"
            class="min-w-[250px] max-w-[350px]"
          ></Select>
        </div>
        <div
          v-else
          class="flex"
        >
          <Icon
            type="location-dot"
            class="mr-1"
          />
          <div>{{ dealershipsAllowed[0]?.name }}</div>
        </div>
        <div>
          <DatePicker
            v-model="dateRange"
            class="w-full"
            selection-mode="range"
            :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
            close-on-select
            show-presets
            :presets="dateRangePresets"
            @select="onDateRangeChange"
          />
        </div>
      </div>
    </template>
  </PageHeader>

  <div
    v-if="dealershipSelected"
    class="grid sm:grid-cols-1 lg:grid-cols-3 lg:gap-6"
  >
    <div class="">
      <Card :is-loading="fetchDataRequest.isLoading.value">
        <CardBody>
          <CardTitle title="Actionable Items" />
          <ListGroup>
            <ListGroupItem
              has-chevron
              hover-effect
              :route="`/appointments/manager?dealerships=${dealershipSelected}&actionStatus=unassigned`"
            >
              <div class="flex justify-between w-full items-center pr-2">
                <div class="w-full">Unassigned Appointments</div>
                <Chip
                  v-if="dashboardData.appointmentsUnassigned"
                  severity="brand"
                  size="sm"
                >
                  {{ dashboardData.appointmentsUnassigned }}
                </Chip>
              </div>
            </ListGroupItem>

            <ListGroupItem
              has-chevron
              hover-effect
              :route="`/appointments/manager?dealerships=${dealershipSelected}`"
            >
              <div class="flex justify-between w-full items-center pr-2">
                <div class="w-full">Appointments Without Outcome</div>
                <Chip
                  v-if="dashboardData.appointmentsWithoutOutcome"
                  severity="brand"
                  size="sm"
                >
                  {{ dashboardData.appointmentsWithoutOutcome }}
                </Chip>
              </div>
            </ListGroupItem>
          </ListGroup>
        </CardBody>
      </Card>

      <Card :is-loading="fetchDataRequest.isLoading.value">
        <CardBody>
          <CardTitle title="Upcoming Appointments" />
          <ListGroup>
            <ListGroupItem
              has-chevron
              hover-effect
              :route="'/appointments/calendar?view=timeGridDay&date=' + dayjs().format('YYYY-MM-DD')"
            >
              <div class="flex justify-between w-full items-center pr-2">
                <div class="w-full">Today</div>
                <Chip
                  v-if="dashboardData.appointmentsToday"
                  severity="brand"
                  size="sm"
                >
                  {{ dashboardData.appointmentsToday }}
                </Chip>
              </div>
            </ListGroupItem>

            <ListGroupItem
              has-chevron
              hover-effect
              :route="'/appointments/calendar?view=timeGridDay&date=' + dayjs().add(1, 'day').format('YYYY-MM-DD')"
            >
              <div class="flex justify-between w-full items-center pr-2">
                <div class="w-full">Tomorrow</div>
                <Chip
                  v-if="dashboardData.appointmentsTomorrow"
                  severity="brand"
                  size="sm"
                >
                  {{ dashboardData.appointmentsTomorrow }}
                </Chip>
              </div>
            </ListGroupItem>

            <ListGroupItem
              has-chevron
              hover-effect
              :route="'/appointments/calendar?view=timeGridWeek&date=' + dayjs().format('YYYY-MM-DD')"
            >
              <div class="flex justify-between w-full items-center pr-2">
                <div class="w-full">This Week</div>
                <Chip
                  v-if="dashboardData.appointmentsThisWeek"
                  severity="brand"
                  size="sm"
                >
                  {{ dashboardData.appointmentsThisWeek }}
                </Chip>
              </div>
            </ListGroupItem>
          </ListGroup>
        </CardBody>
      </Card>

      <!-- <div v-if="dealershipSelected" class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="flex flex-col sm:flex-row lg:flex-col gap-4 col-span-2 lg:col-span-1">
          <div class="w-full">
            <Card>
              <CardBody v-if="dashboardData.actionScore" class="flex border-t border-light">
                <div class="w-full">
                  <div class="text-lg font-medium w-full mb-1">Action Score</div>
                  <div class="text-sm text-secondary">It takes your team <b>4 days (avg)</b> to action things</div>
                </div>
                <div class="-mt-4">
                  <apexchart type="radialBar" :options="actionScoreChart.options" :series="actionScoreChart.series"></apexchart>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div> -->
    </div>
    <div class="lg:col-span-2">
      <Card :is-loading="fetchDataRequest.isLoading.value">
        <CardBody>
          <CardTitle title="Calls" />
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Statistic
              v-model="dashboardData.callsMade"
              label="Calls Made"
              icon="phone-arrow-up-right"
              color="brand"
              :show-difference="false"
            />
            <Statistic
              v-model="dashboardData.callsAnswered"
              label="Answered"
              icon="phone-volume"
              color="info"
              :show-difference="false"
            />
            <Statistic
              v-model="dashboardData.callsOpportunities"
              label="Opportunity"
              icon="user-check"
              color="info"
              :show-difference="false"
            />
            <Statistic
              v-model="dashboardData.appointmentsBooked"
              label="Booked"
              icon="calendar-plus"
              color="success"
              :show-difference="false"
            />
          </div>
        </CardBody>
      </Card>
      <Card :is-loading="fetchDataRequest.isLoading.value">
        <CardBody>
          <CardTitle title="Appointments" />
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Statistic
              v-model="dashboardData.appointmentsOccurred"
              label="Occurred"
              icon="calendar"
              color="brand"
              :show-difference="false"
            />
            <Statistic
              v-model="dashboardData.appointmentsAttended"
              label="Attended"
              icon="user"
              color="info"
              :show-difference="false"
            />
            <Statistic
              v-model="dashboardData.appointmentsRenewed"
              label="Renewed"
              icon="check"
              color="success"
              :show-difference="false"
            />
            <Statistic
              v-model="dashboardData.appointmentsNoShow"
              label="No-Show"
              icon="xmark"
              color="danger"
              :show-difference="false"
            />
          </div>
        </CardBody>
      </Card>

      <Card
        :is-loading="fetchDataRequest.isLoading.value"
        class="lg:col-span-2"
      >
        <div class="grid grid-cols-1 md:grid-cols-3">
          <CardBody class="col-span-2">
            <CardTitle
              title="Conversions"
              :has-margin="true"
            />
            <apexchart
              ref="conversionHistoryChartRef"
              type="area"
              height="230"
              :options="conversionHistoryChart.options"
              :series="conversionHistoryChart.series"
            ></apexchart>
          </CardBody>
          <div class="border-t md:border-l md:border-t-0 border-secondary">
            <CardBody class="text-center border-b border-secondary flex justify-center">
              <div class="max-w-64">
                <apexchart
                  type="radialBar"
                  :options="currentConversionPercentageChart.options"
                  :series="currentConversionPercentageChart.series"
                ></apexchart>
                <div class="text-sm text-secondary mt-2">Current Converstion Rate</div>
              </div>
            </CardBody>
            <CardBody>
              <div class="flex flex-col items-center">
                <Avatar
                  icon="medal"
                  size="lg"
                />
                <div class="text-sm text-secondary mt-2">Best Performer</div>
                <div
                  v-if="dashboardData.bestPerformer"
                  class="flex flex-col items-center"
                >
                  <div class="text-lg font-medium">{{ dashboardData.bestPerformer.userName }}</div>
                  <div v-tippy="{ content: 'Conversion Rate' }">
                    {{ dashboardData.bestPerformer.conversionRate * 100 }}%
                  </div>
                </div>
                <div v-else>N/A</div>
              </div>
            </CardBody>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, watchEffect } from 'vue'
import dayjs from 'dayjs'

import { formatNumber } from '@/utilities/number'
import Select from '@/components/forms/Select.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Chip from '@/components/chip/Chip.vue'
import Statistic from '@/components/dashboard/stats/Statistic.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import { useBreakPoints } from '@/composables/useWindowSize'

import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Avatar from '@/components/avatar/Avatar.vue'

import apexChartDefaults from '@/components/charts/apexChartDefaults'
import Icon from '@/components/icon/Icon.vue'

const authStore = useAuthStore()
const dealershipSelected = ref('')

const dealershipsAllowed = authStore.accessAllowed.dealerships
const dealershipsSelectedLS = parseInt(localStorage.getItem('dealership-dashboard-dealerships-selected'))

// Reactive effect to watch for changes in dealershipsAllowed
watchEffect(() => {
  // Ensure dealershipsAllowed is populated
  if (dealershipsAllowed && dealershipsAllowed.length > 0) {
    // Default to the first dealership if no valid selection is found in localStorage
    dealershipSelected.value = dealershipsAllowed[0].id

    if (dealershipsSelectedLS && dealershipsAllowed.some(dealership => dealership.id === dealershipsSelectedLS)) {
      dealershipSelected.value = dealershipsSelectedLS
    }
  }
})

const defaultDashboardData = {
  appointmentsToday: 0,
  callsMade: 0,
  appointmentsOccurred: 0,
  appointmentsAttended: 0,
  appointmentsRenewed: 0,
  appointmentsBooked: 0,
  valueRenewed: 0,
  appointmentsUnassigned: 0,
  appointmentsWithoutOutcome: 0,
  conversionHistory: [],
  currentConversionRate: 0,
  bestPerformer: {
    userName: null,
    conversionRate: 0
  }
}

const dashboardData = ref(defaultDashboardData)

const fetchDataRequest = useApiRequest()

const conversionHistoryChartRef = ref(null)

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().startOf('day').format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  }
]

function getDashboardData() {
  if (!dealershipSelected.value || !dateRange.value[0] || !dateRange.value[1]) {
    return
  }
  // Reset the dashboard data
  dashboardData.value = defaultDashboardData

  fetchDataRequest
    .send({
      endpoint: '/v1/dashboards/dealership',
      method: 'get',
      params: {
        dealership: dealershipSelected.value,
        start: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
        end: dayjs(dateRange.value[1]).format('YYYY-MM-DD')
      }
    })
    .then(response => {
      dashboardData.value = response.data

      currentConversionPercentageChart.value.series = [
        formatNumber(dashboardData.value.currentConversionRate * 100, 'normal', 2)
      ]

      // empty the series
      conversionHistoryChart.value.series[0].data = []
      conversionHistoryChart.value.series[1].data = []

      const categories = []

      dashboardData.value.conversionHistory.forEach(month => {
        conversionHistoryChart.value.series[0].data.push(month.total)
        conversionHistoryChart.value.series[1].data.push(month.renewed)
        categories.push(month.month)
      })

      if (conversionHistoryChartRef.value) {
        conversionHistoryChartRef.value.updateOptions({
          xaxis: {
            categories: categories
          }
        })
      }
    })
}
getDashboardData()

// fetch the data when the selected dealership changes
watch(
  dealershipSelected,
  () => {
    getDashboardData()
    // Store selected dealership in local storage
    localStorage.setItem('dealership-dashboard-dealerships-selected', dealershipSelected.value)
  },
  { deep: true }
)

const conversionHistoryChart = ref({
  ...apexChartDefaults.lineAreaChartSpline,
  options: {
    ...apexChartDefaults.lineAreaChartSpline.options,
    xaxis: {
      ...apexChartDefaults.lineAreaChartSpline.options.xaxis,
      categories: []
    }
  },
  series: [
    {
      name: 'Appointments',
      data: []
    },
    {
      name: 'Renewals',
      data: []
    }
  ]
})

const currentConversionPercentageChart = ref({
  ...apexChartDefaults.halfGauge,
  options: {
    ...apexChartDefaults.halfGauge.options,
    fill: {
      colors: ['var(--bg-error-solid)']
    }
  },
  series: [36]
})

function onDateRangeChange() {
  getDashboardData()
}
</script>

<style></style>
